import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ChevronLeft, X } from "lucide-react";

const STEPS = [
  {
    target: ".capture-column .section-card",
    title: "Capture",
    body: "Type a task and press Enter to create a bubble. Choose Daily, Weekly, or Monthly — the brighter the color, the more urgent.",
    position: "right",
  },
  {
    target: ".template-section",
    title: "Templates",
    body: "Save tasks you repeat often as templates. One click to re-add them anytime — no retyping.",
    position: "right",
  },
  {
    target: ".matrix-column .section-card",
    title: "Priority Matrix",
    body: "Drag bubbles into quadrants — or use the arrow menu. High Impact + Low Effort tasks go top-left: your Quick Wins.",
    position: "right",
  },
  {
    target: '[data-testid="energy-level-toggle"]',
    title: "Energy Level",
    body: "Set your energy for the session. The to-do list reorders itself — low energy avoids high-effort tasks to keep you moving.",
    position: "bottom",
  },
  {
    target: ".list-column .section-card",
    title: "Next Up",
    body: "Your auto-prioritized to-do list. Click any task with sub-tasks to expand and check them off inline. Done.",
    position: "left",
  },
];

const STORAGE_KEY = "prism_onboarding_done";

export default function OnboardingTour({ active, onComplete }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  const measureTarget = useCallback(() => {
    if (!active) return;
    const el = document.querySelector(STEPS[step].target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step, active]);

  useEffect(() => {
    measureTarget();
    window.addEventListener("resize", measureTarget);
    window.addEventListener("scroll", measureTarget, true);
    return () => {
      window.removeEventListener("resize", measureTarget);
      window.removeEventListener("scroll", measureTarget, true);
    };
  }, [measureTarget]);

  // Re-measure when step changes (with slight delay for layout)
  useEffect(() => {
    const timer = setTimeout(measureTarget, 100);
    return () => clearTimeout(timer);
  }, [step, measureTarget]);

  if (!active || !rect) return null;

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const pad = 8;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    const style = {};
    const tooltipWidth = 300;
    const tooltipGap = 16;

    switch (current.position) {
      case "right":
        style.top = rect.top + rect.height / 2;
        style.left = rect.left + rect.width + tooltipGap;
        style.transform = "translateY(-50%)";
        break;
      case "left":
        style.top = rect.top + rect.height / 2;
        style.left = rect.left - tooltipWidth - tooltipGap;
        style.transform = "translateY(-50%)";
        break;
      case "bottom":
        style.top = rect.top + rect.height + tooltipGap;
        style.left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case "top":
        style.top = rect.top - tooltipGap;
        style.left = rect.left + rect.width / 2 - tooltipWidth / 2;
        style.transform = "translateY(-100%)";
        break;
      default:
        style.top = rect.top + rect.height + tooltipGap;
        style.left = rect.left;
    }

    // Clamp to viewport
    if (style.left < 16) style.left = 16;
    if (style.left + tooltipWidth > window.innerWidth - 16) {
      style.left = window.innerWidth - tooltipWidth - 16;
    }
    // Vertical clamping
    const estimatedHeight = 180;
    if (style.top < 16) style.top = 16;
    if (style.transform !== "translateY(-100%)" && style.top + estimatedHeight > window.innerHeight - 16) {
      style.top = window.innerHeight - estimatedHeight - 16;
    }

    return style;
  };

  const handleNext = () => {
    if (isLast) {
      localStorage.setItem(STORAGE_KEY, "true");
      onComplete();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) setStep((s) => s - 1);
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    onComplete();
  };

  return (
    <div className="tour-overlay">
      {/* Spotlight cutout via CSS clip-path */}
      <div
        className="tour-backdrop"
        onClick={handleSkip}
        style={{
          clipPath: `polygon(
            0% 0%, 0% 100%, 
            ${rect.left - pad}px 100%, 
            ${rect.left - pad}px ${rect.top - pad}px, 
            ${rect.left + rect.width + pad}px ${rect.top - pad}px, 
            ${rect.left + rect.width + pad}px ${rect.top + rect.height + pad}px, 
            ${rect.left - pad}px ${rect.top + rect.height + pad}px, 
            ${rect.left - pad}px 100%, 
            100% 100%, 100% 0%
          )`,
        }}
      />

      {/* Spotlight ring */}
      <div
        className="tour-spotlight"
        style={{
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
        }}
      />

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          ref={tooltipRef}
          className="tour-tooltip"
          style={{ ...getTooltipStyle(), width: 300 }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {/* Step indicator */}
          <div className="tour-step-indicator">
            {STEPS.map((s, i) => (
              <span key={s.target || i} className={`tour-dot ${i === step ? "active" : ""} ${i < step ? "done" : ""}`} />
            ))}
          </div>

          <h3 className="tour-title">{current.title}</h3>
          <p className="tour-body">{current.body}</p>

          <div className="tour-actions">
            <button className="tour-skip-btn" onClick={handleSkip}>
              Skip tour
            </button>
            <div className="tour-nav">
              {!isFirst && (
                <button className="tour-nav-btn" onClick={handlePrev}>
                  <ChevronLeft size={14} />
                </button>
              )}
              <button className="tour-next-btn" onClick={handleNext}>
                {isLast ? "Get started" : "Next"}
                {!isLast && <ChevronRight size={14} />}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export { STORAGE_KEY };
